import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import * as XLSX from "xlsx";

import { exportLeavesReport } from "../../../../../../../services/attendanceService";
import { notify } from "../../../../../../../services/toast.config";

import type {
  CompanyOption,
  EmployeeOption,
  LeaveTypeOption,
} from "../../../../../../../core/data/redux/admin-leaves/types";

interface ExportLeavesModalProps {
  show: boolean;
  selectedCompany: CompanyOption | null;
  selectedEmployee: EmployeeOption | null;
  selectedLeaveType: LeaveTypeOption | null;
  onHide: () => void;
}

const DEFAULT_START = () => dayjs().subtract(1, "month");
const DEFAULT_END = () => dayjs();

const ExportLeavesModal: React.FC<ExportLeavesModalProps> = ({
  show,
  selectedCompany,
  selectedEmployee,
  selectedLeaveType,
  onHide,
}) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(DEFAULT_START());
  const [endDate, setEndDate] = useState<Dayjs | null>(DEFAULT_END());
  const [status, setStatus] = useState<string>("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setStartDate(DEFAULT_START());
      setEndDate(DEFAULT_END());
      setStatus("all");
    }
  }, [show]);

  const handleClose = () => {
    setStartDate(DEFAULT_START());
    setEndDate(DEFAULT_END());
    setStatus("all");
    onHide();
  };

  const handleExport = async () => {
    if (!selectedCompany) {
      notify.error("Please select a company first");
      return;
    }
    if (!startDate || !endDate) {
      notify.error("Please select both start and end dates");
      return;
    }
    if (startDate.isAfter(endDate)) {
      notify.error("Start date cannot be after end date");
      return;
    }
    if (endDate.isAfter(startDate.add(1, "year"))) {
      notify.error("Date range cannot exceed 1 year");
      return;
    }

    setLoading(true);
    try {
      const companyParam = {
        value: selectedCompany.companyId,
        companyType: selectedCompany.companyType,
      } as any;
      const employeeParam: any = selectedEmployee?.value
        ? selectedEmployee
        : null;
      const leaveTypeIdValue = selectedLeaveType?.value || null;

      const result = await exportLeavesReport(
        startDate.format("YYYY-MM-DD"),
        endDate.format("YYYY-MM-DD"),
        companyParam,
        null,
        employeeParam,
        status,
        leaveTypeIdValue as any,
        "excel",
      );

      const rows = result?.data?.data;
      if (Array.isArray(rows) && rows.length > 0) {
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Leaves Report");
        const fileName = `leaves-report-${startDate.format("YYYY-MM-DD")}-to-${endDate.format(
          "YYYY-MM-DD",
        )}.xlsx`;
        XLSX.writeFile(wb, fileName);
        notify.success(
          `Leaves report exported successfully (${rows.length} records)`,
        );
        handleClose();
      } else if (Array.isArray(rows) && rows.length === 0) {
        notify.info("No leave records found for the selected filters");
      } else if (result?.data?.message) {
        notify.error(result.data.message);
      } else {
        notify.error("No data found or invalid response format");
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to export leaves report";
      notify.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Export Leaves Report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  Start Date <span className="text-danger">*</span>
                </Form.Label>
                <DatePicker
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  format="DD MMM YYYY"
                  placeholder="Select start date"
                  className="w-100"
                  style={{ height: "38px" }}
                  allowClear
                />
                {!startDate && (
                  <div className="text-danger small mt-1">
                    Start date is required
                  </div>
                )}
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  End Date <span className="text-danger">*</span>
                </Form.Label>
                <DatePicker
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  format="DD MMM YYYY"
                  placeholder="Select end date"
                  className="w-100"
                  style={{ height: "38px" }}
                  allowClear
                  disabledDate={(current) => {
                    if (!startDate || !current) return false;
                    return current.isBefore(startDate, "day");
                  }}
                />
                {!endDate && (
                  <div className="text-danger small mt-1">
                    End date is required
                  </div>
                )}
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Export Format</Form.Label>
                <Form.Select defaultValue="excel">
                  <option value="excel">Excel (CSV)</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <Form.Group className="mb-3">
                <Form.Label>Applied Filters</Form.Label>
                <div className="border rounded p-3 bg-light">
                  <div className="mb-2">
                    <strong>Company:</strong>{" "}
                    {selectedCompany?.label || "All Companies"}
                  </div>
                  {selectedEmployee?.value && (
                    <div className="mb-2">
                      <strong>Employee:</strong> {selectedEmployee.label}
                    </div>
                  )}
                  {selectedLeaveType?.value && (
                    <div className="mb-2">
                      <strong>Leave Type:</strong> {selectedLeaveType.label}
                    </div>
                  )}
                  <div>
                    <strong>Date Range:</strong>{" "}
                    {startDate?.format("DD/MM/YYYY")} to{" "}
                    {endDate?.format("DD/MM/YYYY")}
                  </div>
                </div>
              </Form.Group>
            </div>
          </div>

          <div className="alert alert-info mb-0">
            <i className="ti ti-info-circle me-2"></i>
            This will export all leave records matching the selected filters.
            Large date ranges may take longer to process.
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Exporting...
            </>
          ) : (
            <>
              <i className="ti ti-download me-1"></i> Export Report
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportLeavesModal;
